import { DatePipe, DecimalPipe, LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { downloadCsv } from '../../core/functions/csv-export';
import { initials, initialsFromName } from '../../core/functions/initials';
import { dayLabel } from '../../core/functions/relative-time';
import { AuthService } from '../../core/services/auth/auth.service';
import { Release } from '../../models/release';
import {
  AuditActionType,
  InsightsAuditEntry,
  InsightsFailure,
  InsightsOverview,
  InsightsTester,
} from '../../models/insights';
import { TestSeverity } from '../../models/test-case-result-insert-request';
import { ReleaseService } from '../../services/release/release.service';
import { ReleaseContextService } from '../../services/release-context/release-context.service';
import { InsightsService } from '../../services/insights/insights.service';
import { ToastService } from '../../ui';

type InsightsTab = 'overview' | 'testers' | 'failures' | 'audit';
type SeverityFilter = 'All' | TestSeverity;
type TesterFilter = 'All' | 'Active' | 'Inactive';
type AuditFilter = 'All' | 'Passes' | 'Failures' | 'Skips' | 'Assignments' | 'Visibility' | 'Comments' | 'Triage';

/**
 * Which action types each chip covers (PBI 57764 AC4). The audit now carries more
 * than pass/fail, and grouping keeps the chip row usable rather than growing one
 * chip per action type.
 *
 * Comments and Triage are declared ahead of the PBIs that write them (57768, 57769,
 * 57770). Chips only render when they have entries, so these stay hidden until
 * those items ship and then appear on their own.
 */
const AUDIT_GROUPS: Record<Exclude<AuditFilter, 'All'>, readonly AuditActionType[]> = {
  Passes: ['Passed'],
  Failures: ['Failed'],
  Skips: ['Skipped'],
  Assignments: ['Assigned', 'Unassigned', 'Reassigned'],
  Visibility: ['PlanSetLive', 'PlanSetNotLive', 'TestCaseSetLive', 'TestCaseSetNotLive'],
  Comments: ['CommentAdded', 'CommentEdited', 'CommentDeleted'],
  Triage: ['FailureStatusChanged', 'FailureSeverityChanged'],
};

/** Passes and Failures stay visible even at zero, so the tab reads as it does today. */
const ALWAYS_SHOWN_AUDIT_CHIPS: readonly AuditFilter[] = ['All', 'Passes', 'Failures'];

/**
 * How each non-result action reads in the log. Pass and fail keep their existing
 * "marked X as [Passed]" phrasing in the template, so they are not listed here.
 */
const AUDIT_VERBS: Partial<Record<AuditActionType, string>> = {
  Skipped: 'skipped',
  Assigned: 'assigned',
  Unassigned: 'unassigned',
  Reassigned: 'reassigned',
  PlanSetLive: 'made live',
  PlanSetNotLive: 'hid',
  TestCaseSetLive: 'made live',
  TestCaseSetNotLive: 'hid',
  CommentAdded: 'commented on',
  CommentEdited: 'edited a comment on',
  CommentDeleted: 'deleted a comment on',
  FailureStatusChanged: 'changed the status of',
  FailureSeverityChanged: 'changed the severity of',
};

interface AuditDay {
  label: string;
  entries: InsightsAuditEntry[];
}

/**
 * Insights (PBI 55102–55105). A single tabbed page — Overview / Testers / Failures /
 * Audit. Data comes from the Insights API (or mock data in local dev). No charting
 * library: KPI tiles + CSS bars + lists, per the design.
 *
 * Which release it shows comes from the `releaseId` query param, defaulting to the
 * active one. The header's release switcher writes that param, so changing release
 * reloads the data in place and keeps the tab you were on (Bug 56801).
 */
@Component({
  selector: 'app-insights',
  standalone: true,
  imports: [DatePipe, DecimalPipe, LowerCasePipe, RouterLink],
  templateUrl: './insights.component.html',
  styleUrl: './insights.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsightsComponent implements OnInit {
  private releaseService = inject(ReleaseService);
  private insightsService = inject(InsightsService);
  private releaseContext = inject(ReleaseContextService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly tab = signal<InsightsTab>('overview');
  readonly selectedRelease = signal<Release | null>(null);
  readonly isLoading = signal(true);
  readonly loadError = signal(false);

  /** The `releaseId` currently reflected in the URL, so a tab-only change to the
      query string doesn't re-fetch everything. */
  private requestedReleaseId: number | null = null;

  readonly overview = signal<InsightsOverview | null>(null);
  readonly testers = signal<InsightsTester[]>([]);
  readonly failures = signal<InsightsFailure[]>([]);
  readonly audit = signal<InsightsAuditEntry[]>([]);

  readonly search = signal('');
  readonly severityFilter = signal<SeverityFilter>('All');
  readonly testerFilter = signal<TesterFilter>('All');
  readonly auditFilter = signal<AuditFilter>('All');

  readonly severityChips: SeverityFilter[] = ['All', 'Showstopper', 'Blocker', 'Functional', 'Cosmetic'];
  readonly testerChips: TesterFilter[] = ['All', 'Active', 'Inactive'];

  readonly auditCounts = computed(() => {
    const all = this.audit();
    const counts = { All: all.length } as Record<AuditFilter, number>;
    for (const [group, types] of Object.entries(AUDIT_GROUPS)) {
      counts[group as AuditFilter] = all.filter((e) => types.includes(e.actionType)).length;
    }
    return counts;
  });

  /**
   * All, Passes and Failures always; every other group only once it has something in
   * it, so the row does not fill with chips that match nothing.
   */
  readonly auditChips = computed<AuditFilter[]>(() => {
    const counts = this.auditCounts();
    const optional = (Object.keys(AUDIT_GROUPS) as AuditFilter[])
      .filter((g) => !ALWAYS_SHOWN_AUDIT_CHIPS.includes(g) && counts[g] > 0);
    return [...ALWAYS_SHOWN_AUDIT_CHIPS, ...optional];
  });

  ngOnInit(): void {
    // A bookmarked link can carry both, so take the tab once up front and then
    // follow the release through query-param changes.
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (isInsightsTab(tab)) this.tab.set(tab);

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const releaseId = parseReleaseId(params.get('releaseId'));
      // Ignore emissions that only changed the tab; the data is already right.
      if (this.selectedRelease() && releaseId === this.requestedReleaseId) return;
      this.fetch(releaseId);
    });
  }

  setTab(t: InsightsTab): void {
    this.tab.set(t);
    this.search.set('');
    // Keep the tab in the URL so a bookmarked link comes back to the same tab and
    // release. replaceUrl, so flipping tabs doesn't fill up the back button.
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: t },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Retry hook for the failure state; re-runs the release currently asked for. */
  load(): void {
    this.fetch(this.requestedReleaseId);
  }

  private fetch(requestedReleaseId: number | null): void {
    this.requestedReleaseId = requestedReleaseId;
    this.isLoading.set(true);
    this.loadError.set(false);
    this.releaseService.getReleases().subscribe({
      next: (releases) => {
        // Hand the list to the header switcher so it doesn't fetch it again.
        this.releaseContext.setReleases(releases);

        const requested =
          requestedReleaseId != null
            ? (releases.find((r) => r.releaseId === requestedReleaseId) ?? null)
            : null;
        const selected = requested ?? releases.find((r) => r.isActive) ?? releases[0] ?? null;

        this.selectedRelease.set(selected);
        this.releaseContext.setCurrent(selected);
        if (!selected) {
          this.isLoading.set(false);
          return;
        }
        this.loadInsights(selected.releaseId);
      },
      error: () => {
        this.loadError.set(true);
        this.isLoading.set(false);
      },
    });
  }

  private loadInsights(releaseId: number): void {
    forkJoin({
      overview: this.insightsService.getOverview(releaseId),
      testers: this.insightsService.getTesters(releaseId),
      failures: this.insightsService.getFailures(releaseId),
      audit: this.insightsService.getAudit(releaseId),
    })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (r) => {
          this.overview.set(r.overview);
          this.testers.set(r.testers);
          this.failures.set(r.failures);
          this.audit.set(r.audit);
        },
        error: () => this.loadError.set(true),
      });
  }

  // ---- filtered views ----

  readonly filteredFailures = computed(() => {
    const term = this.search().trim().toLowerCase();
    const sev = this.severityFilter();
    return this.failures()
      .filter((f) => sev === 'All' || f.severity === sev)
      .filter((f) => !term || f.testCaseName.toLowerCase().includes(term) || f.planName.toLowerCase().includes(term));
  });

  readonly severityCounts = computed(() => {
    const all = this.failures();
    return {
      All: all.length,
      Showstopper: all.filter((f) => f.severity === 'Showstopper').length,
      Blocker: all.filter((f) => f.severity === 'Blocker').length,
      Functional: all.filter((f) => f.severity === 'Functional').length,
      Cosmetic: all.filter((f) => f.severity === 'Cosmetic').length,
    } as Record<SeverityFilter, number>;
  });

  readonly filteredTesters = computed(() => {
    const term = this.search().trim().toLowerCase();
    const f = this.testerFilter();
    return this.testers()
      .filter((t) => f === 'All' || (f === 'Active' ? t.isActive : !t.isActive))
      .filter((t) => !term || `${t.firstName} ${t.lastName}`.toLowerCase().includes(term));
  });

  readonly testerCounts = computed(() => {
    const all = this.testers();
    return {
      All: all.length,
      Active: all.filter((t) => t.isActive).length,
      Inactive: all.filter((t) => !t.isActive).length,
    } as Record<TesterFilter, number>;
  });

  readonly filteredAudit = computed(() => {
    const term = this.search().trim().toLowerCase();
    const f = this.auditFilter();
    // A chip can stop being rendered when the release changes and its group empties.
    // Fall back to All rather than silently showing nothing under a filter that is no
    // longer on screen.
    const types = f === 'All' || !this.auditChips().includes(f) ? null : AUDIT_GROUPS[f];
    return this.audit()
      .filter((e) => types === null || types.includes(e.actionType))
      .filter((e) => !term || e.itemName.toLowerCase().includes(term) || e.userName.toLowerCase().includes(term));
  });

  /** Audit entries grouped by day with Today / Yesterday / date labels. */
  readonly auditDays = computed<AuditDay[]>(() => {
    const byKey = new Map<string, AuditDay>();
    for (const e of this.filteredAudit()) {
      const d = new Date(e.timeUtc);
      const key = d.toDateString();
      if (!byKey.has(key)) byKey.set(key, { label: dayLabel(d), entries: [] });
      byKey.get(key)!.entries.push(e);
    }
    return [...byKey.values()];
  });

  // ---- presentation helpers ----

  readonly initials = initials;
  readonly initialsFromName = initialsFromName;

  /**
   * How a non-result audit entry reads, e.g. "skipped" in "Jo Bloggs skipped Login".
   * Pass and fail keep their own phrasing in the template. An action type the UI has
   * not been taught falls back to a neutral verb rather than rendering a blank row.
   */
  actionVerb(actionType: AuditActionType): string {
    return AUDIT_VERBS[actionType] ?? 'changed';
  }

  planPct(passing: number, total: number): number {
    return total > 0 ? Math.round((passing / total) * 100) : 0;
  }

  failPct(failing: number, total: number): number {
    return total > 0 ? Math.round((failing / total) * 100) : 0;
  }

  sevClass(sev: TestSeverity | null): string {
    return sev ? sev.toLowerCase() : 'cosmetic';
  }

  resetFilters(): void {
    this.search.set('');
    this.severityFilter.set('All');
    this.testerFilter.set('All');
    this.auditFilter.set('All');
  }

  /** Export the current tab's rows to CSV (PBI 55106). */
  exportCsv(): void {
    let rows: string[][] = [];
    let name = 'insights';
    if (this.tab() === 'audit') {
      // The audit tab previously fell through to the testers export, so exporting from
      // it produced the wrong file entirely (PBI 57764 AC4).
      name = 'audit';
      rows = [
        ['Time', 'User', 'Action', 'Item', 'Plan', 'Severity', 'Detail'],
        ...this.filteredAudit().map((e) => [
          new Date(e.timeUtc).toLocaleString(),
          e.userName,
          e.actionType,
          e.itemName,
          e.planName,
          e.severity ?? '',
          e.detail ?? '',
        ]),
      ];
    } else if (this.tab() === 'failures') {
      name = 'failures';
      rows = [
        ['Severity', 'Failure', 'Plan', 'Reporter', 'Status', 'Filed'],
        ...this.filteredFailures().map((f) => [
          f.severity ?? '',
          f.testCaseName,
          f.planName,
          f.reporterName ?? '',
          f.status,
          new Date(f.filedOnUtc).toLocaleDateString(),
        ]),
      ];
    } else {
      name = 'testers';
      rows = [
        ['Tester', 'Active', 'Completed', 'Pass rate %', 'Failures filed', 'Current plan'],
        ...this.filteredTesters().map((t) => [
          `${t.firstName} ${t.lastName}`,
          t.isActive ? 'Yes' : 'No',
          String(t.completedCount),
          String(t.passRatePct),
          String(t.failuresFiled),
          t.currentPlanName ?? '',
        ]),
      ];
    }
    downloadCsv(rows, this.selectedRelease()?.releaseName, name);
  }
}

/** Narrow a `?tab=` value; anything else falls back to the default tab. */
function isInsightsTab(value: string | null): value is InsightsTab {
  return value === 'overview' || value === 'testers' || value === 'failures' || value === 'audit';
}

/** A `?releaseId=` value, or null for "whatever is active" (including junk). */
function parseReleaseId(value: string | null): number | null {
  if (value == null) return null;
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}
